import React, { useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { Box, HStack } from "@chakra-ui/layout";

import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { VenuesTable } from "../../components/Venues/VenuesTable";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { Checkbox } from "@chakra-ui/checkbox";
import { useCheckboxState } from "../../hooks/useCheckboxState";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { Button, SkeletonText, VStack } from "@chakra-ui/react";
import { PlusIcon } from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { useSearchInput } from "../../hooks/useSearchInput";
import { BusinessMenu } from "../../components/Business/BusinessMenu";

export const Venues = () => {
  const business = useBusinessContext();
  const navigate = useNavigate();
  const apiClient = useAdminApiClient();
  const { checked: showDeletedVenues, onChange: onChangeDeletedVenuesCheck } =
    useCheckboxState(false);

  const { showErrorToast, showSuccessToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search venues...",
  });

  const {
    items: venues,
    refresh,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getBusinessVenuesResultSet(business.BusinessId!, {
        q: debouncedQuery,
        includeDeleted: showDeletedVenues ? "true" : "false",
      }),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getBusinessVenuesResultSet(business.BusinessId!, {
        q: debouncedQuery,
        includeDeleted: showDeletedVenues ? "true" : "false",
        continuationToken,
      }),
    [business.BusinessId, debouncedQuery, showDeletedVenues]
  );

  const deleteVenueRequest = useAdminApiUpdateRequest(
    (apiClient, venueId: string) =>
      apiClient.deleteBusinessVenue(business.BusinessId!, venueId),
    [venues]
  );

  const restoreVenueRequest = useAdminApiUpdateRequest(
    (apiClient, venueId: string) =>
      apiClient.restoreBusinessVenue(business.BusinessId!, venueId),
    [venues]
  );

  const getVenueRequest = useAdminApiUpdateRequest(
    (apiClient, venueId: string) =>
      apiClient.getBusinessVenue(business.BusinessId!, venueId),
    [venues]
  );

  const onClickNewVenue = () =>
    navigate(`/admin/businesses/${business.BusinessId}/venues/new`);

  const onClickEditVenue = (venue: CherryHubAdminApi.VenueSummaryItem) =>
    navigate(
      `/admin/businesses/${business.BusinessId}/venues/${venue.Meta?.Id}`
    );

  useEffect(() => {
    if (deleteVenueRequest.result) {
      if (deleteVenueRequest.result.ok) {
        showSuccessToast("Venue deleted.");
        refresh();
      } else {
        showErrorToast(
          deleteVenueRequest.result.message ?? "Failed to delete venue."
        );
      }
    }
  }, [deleteVenueRequest.result, refresh]);

  useEffect(() => {
    if (restoreVenueRequest.result) {
      if (restoreVenueRequest.result.ok) {
        showSuccessToast("Deleted Venue restored.");
        refresh();
      } else {
        showErrorToast(
          restoreVenueRequest.result.message ??
            "Failed to restore deleted venue."
        );
      }
    }
  }, [restoreVenueRequest.result, refresh]);

  const onClickDeleteVenue = useCallback(
    (venue: CherryHubAdminApi.VenueSummaryItem) =>
      showConfirmationDialog(
        "Delete Venue?",
        `Are you sure you wish to delete this venue: ${venue.DisplayName}?`,
        "Delete"
      ).then(({ ok }) => {
        if (ok) {
          deleteVenueRequest.request(venue.Meta!.Id!);
        }
      }),
    [
      showSuccessToast,
      showErrorToast,
      showConfirmationDialog,
      apiClient,
      refresh,
    ]
  );

  const onClickRestoreVenue = useCallback(
    (venue: CherryHubAdminApi.VenueSummaryItem) =>
      showConfirmationDialog(
        "Restore Deleted Venue?",
        `Are you sure you wish to restore this deleted venue: ${venue.DisplayName}?`,
        "Restore"
      ).then(({ ok }) => {
        if (ok) {
          restoreVenueRequest.request(venue.Meta!.Id!);
        }
      }),
    [
      showSuccessToast,
      showErrorToast,
      showConfirmationDialog,
      apiClient,
      refresh,
    ]
  );

  const skeletonLoading = (rows: number = 12) => {
    return (
      <HStack width="100%" boxShadow="lg">
        <Box width="5%" pt="4" pb="4">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
        <Box width="85%" padding="4" pr="0">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
        <Box width="5%" pt="4" pb="4">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
        <Box width="5%" pt="4" pb="4" pr="4">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
      </HStack>
    );
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Venues</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading" mb={4}>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/venues`}
              >
                Venues
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          <HStack w="100%">
            <Box flex="4" maxWidth="400px">
              {searchInputElement}
            </Box>
            <Box flex="1">
              <Checkbox
                checked={showDeletedVenues}
                onChange={onChangeDeletedVenuesCheck}
                title="Include deleted venues in the list."
              >
                Include Deleted
              </Checkbox>
            </Box>
            <Box marginLeft="auto">
              <BusinessMenu
                businessId={business.Meta?.Id!}
                leftIcon={<PlusIcon />}
                onClick={onClickNewVenue}
              >
                New Venue
              </BusinessMenu>
            </Box>
          </HStack>

          <VenuesTable
            venues={venues}
            isLoading={isLoading}
            disableActions={
              deleteVenueRequest.isLoading ||
              getVenueRequest.isLoading ||
              restoreVenueRequest.isLoading
            }
            debouncedQuery={debouncedQuery}
            onClickEditVenue={onClickEditVenue}
            onClickDeleteVenue={onClickDeleteVenue}
            onClickRestoreVenue={onClickRestoreVenue}
          />
          {fetchNextPage && !isLoading && (
            <Box w="100%">
              {!isLoadingPage && (
                <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                  Load more...
                </Button>
              )}
              {isLoadingPage && skeletonLoading(4)}
            </Box>
          )}
          {error && (
            <ErrorMessage>
              {error ?? "Error fetching business venues."}
            </ErrorMessage>
          )}
          {(!venues || isLoading) && <LoadingIndicator />}
          {confirmationElement}
        </VStack>
      </PageContent>
    </>
  );
};
